import { zodResolver } from '@hookform/resolvers/zod';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { useTools } from '@risksmart-app/components/src/tools/useTools';
import { CustomAttributeFieldType } from '@risksmart-app/form-configuration/src/field-types/types';
import { getFormConfigRegistry } from '@risksmart-app/shared/forms/formConfigRegistry';
import _ from 'lodash';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  Control,
  DefaultValues,
  FieldValues,
  SubmitHandler,
} from 'react-hook-form';
import { FormProvider, useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useModulesStore } from 'src/context/moduleContext';
import { useCustomDatasourceHelpers } from 'src/pages/custom-datasources/useCustomDatasourceHelpers';
import { useChangeRequestConfirm } from 'src/providers/change-request-confirm-context';
import type { ZodSchema } from 'zod';

import { ActiveFormPublisher } from '@/blocks/active-form/ActiveFormPublisher';
import { useObjectToolbarFormStore } from '@/blocks/object-toolbar/useObjectToolbarFormStore';
import { useGetFileById } from '@/hooks/queries';
import { useChangeRequests } from '@/hooks/useChangeRequests';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import {
  MUTATION_NO_OP,
  mutationResultNotification,
  tryResolveOptimisticLockConflict,
} from '@/hooks/useMutationResultNotification';
import { handleError } from '@/utils/errorUtils';
import { mapFormError, setFormFieldError } from '@/utils/formError';
import { getCustomAttributeRenderProps } from '@/utils/table/utils/customAttributes';

import { ChangeRequestAlerts } from './ChangeRequestAlerts';
import { ConditionalFieldsProvider } from './conditional-fields-provider/ConditionalFieldsProvider';
import {
  buildFieldConditionGraph,
  getEffectiveFieldConditions,
  getHiddenFields,
} from './conditional-fields-provider/conditionsGraph';
import {
  buildStandardFieldCanonicalMap,
  canonicaliseStandardFieldKey,
} from './conditionPropertyKeys';
import { RiskSmartFormProvider } from './customisable-form/RiskSmartFormProvider';
import { useCustomisableFormDataContext } from './customisable-form-data/CustomisableFormDataContext';
import FormActions from './FormActions';
import { mapChanges } from './mapChanges';
import type { FormContextProps, SaveAction } from './types';

// Publishes the active form's save state to the object-toolbar bridge store so
// the page-header toolbar (mounted outside this provider — see
// `useObjectToolbarFormStore`) can drive its save disc. Rendered ONLY when a
// form opts in via `publishToolbarState`, so the reactive `useFormState`
// subscription is scoped to that single form. Every other form thus never
// subscribes to `isDirty`/`isSubmitting`, avoiding the late post-`act()` state
// emit that failed unrelated tests under the shared FormContext (CUS-134).
// Exported for focused lifecycle tests (see ToolbarStatePublisher.test.tsx).
export const ToolbarStatePublisher = <TFieldValues extends FieldValues>({
  control,
  readOnly,
  onSave,
}: {
  control: Control<TFieldValues>;
  readOnly: boolean;
  onSave: () => void;
}) => {
  const { isDirty, isSubmitting } = useFormState({ control });
  const setToolbarFormState = useObjectToolbarFormStore((s) => s.setFormState);
  const clearToolbarFormState = useObjectToolbarFormStore(
    (s) => s.clearFormState
  );

  // `onSave` (FormContext's `defaultOnSave`) is rebuilt on every FormContext
  // render — `methods.handleSubmit(handleSubmitWrapper(...))` returns a fresh
  // reference each time. Holding it in a ref and publishing a *stable* wrapper
  // keeps the effect below out of the per-render churn: its deps then change
  // only when the published values (`readOnly`/`isDirty`/`isSubmitting`) do, so
  // the bridge store is no longer cleared-and-reset every render (which
  // re-rendered every toolbar subscriber and flashed `formState=null` on tab
  // switch). (CUS-134)
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const stableOnSave = useCallback(() => onSaveRef.current(), []);

  useEffect(() => {
    setToolbarFormState({
      supported: !readOnly,
      dirty: isDirty,
      loading: isSubmitting,
      onSave: stableOnSave,
    });

    return () => clearToolbarFormState();
  }, [
    readOnly,
    isDirty,
    isSubmitting,
    stableOnSave,
    setToolbarFormState,
    clearToolbarFormState,
  ]);

  return null;
};

// It sets up methods and state for the form, and passes them down to the `ControlledForm` component
export const FormContext = <TFieldValues extends FieldValues>(
  props: FormContextProps<TFieldValues>
) => {
  const helpers = useCustomDatasourceHelpers();
  const { search } = useLocation();
  const {
    defaultValues: originalDefaultValues,
    values,
    saveMode,
    schema,
    onSave,
    onDelete,
    readOnly,
    renderTemplate,
    submitActions,
    secondaryActions,
    mapPreviewedChanges,
    mapRequestedChanges,
    onDeleteApproved,
    publishToolbarState,
    publishActiveForm,
  } = props;
  const modules = useModulesStore((s) => s.modules);
  const formSectionsEnabled = useIsFeatureFlagEnabled('form_sections');
  // TODO: eventually move this to a higher level
  const customisableFormData = useCustomisableFormDataContext();
  const { formFieldConfigurations } = customisableFormData;
  // form_field_configuration.FieldId for standard fields is persisted in the
  // form editor's key space (lowercase React key, e.g. `title`), while the
  // form values key by canonical FieldId (`Title`). Every other consumer
  // matches case-insensitively; defaults are *written* into form values, so
  // resolve the canonical key first — a default set at the wrong casing is
  // invisible to the controller and shadows the real value in the preview.
  const standardFieldCanonicalById = useMemo(() => {
    const formConfig = props.parentType
      ? getFormConfigRegistry(formSectionsEnabled, modules)[props.parentType]
      : undefined;

    return buildStandardFieldCanonicalMap(formConfig);
  }, [props.parentType, formSectionsEnabled, modules]);
  // could potentially move this into parent hook?
  const defaultValues = useMemo(
    () =>
      (formFieldConfigurations ?? []).reduce<DefaultValues<TFieldValues>>(
        (previous, currentValue) => {
          if (!_.isNil(currentValue.DefaultValue)) {
            _.set(
              previous,
              canonicaliseStandardFieldKey(
                currentValue.FieldId,
                standardFieldCanonicalById
              ),
              currentValue.DefaultValue
            );
          }

          return previous;
        },
        originalDefaultValues
      ),
    [originalDefaultValues, formFieldConfigurations, standardFieldCanonicalById]
  );

  const { t: st } = useTranslation('common');
  const [onSaveFn, setOnSave] = useState<(() => Promise<void>) | undefined>();
  const [beforeSaveHooks, setBeforeSaveHooks] = useState<
    (() => Promise<boolean>)[]
  >([]);
  // Run after the entity mutation resolves successfully, receiving the saved
  // entity id (from the action's return on create, or the form values on
  // edit). Used by the custom-attribute file pipeline to link uploaded files
  // to the parent via `relation_file` once its id is known.
  const [afterSaveHooks, setAfterSaveHooks] = useState<
    ((savedId: string | undefined) => Promise<void>)[]
  >([]);

  const { addNotification } = useNotifications();
  const { consumeChangeRequestCreated } = useChangeRequestConfirm();
  const [viewingChangeRequest, setViewingChangeRequest] = useState(false);
  const [customFormValidation, setCustomFormValidation] = useState<
    (schema: ZodSchema) => ZodSchema
  >(() => (s: ZodSchema) => s);
  const [toolsContent, setToolsContent] = useTools();

  const methods = useForm<TFieldValues>({
    /* @ts-ignore  TS2589: Type instantiation is excessively deep and possibly infinite. */
    resolver: zodResolver(customFormValidation(schema)),
    defaultValues,
    values,
    shouldFocusError: true,
    mode: 'onChange',
  });

  // Apply custom field defaults when formFieldConfigurations change (e.g. when
  // switching between Inherent and Residual result types). react-hook-form's
  // defaultValues only apply on initial mount, so we need to explicitly set
  // values for empty fields when the configuration changes.
  useEffect(() => {
    if (!formFieldConfigurations) {
      return;
    }

    const currentValues = methods.getValues();
    let hasUpdates = false;
    for (const config of formFieldConfigurations) {
      if (!_.isNil(config.DefaultValue)) {
        const fieldKey = canonicaliseStandardFieldKey(
          config.FieldId,
          standardFieldCanonicalById
        );
        const currentValue = _.get(currentValues, fieldKey);
        if (_.isNil(currentValue) || currentValue === '') {
          _.set(currentValues, fieldKey, config.DefaultValue);
          hasUpdates = true;
        }
      }
    }
    if (hasUpdates) {
      methods.reset(currentValues, { keepDirty: true });
    }
  }, [formFieldConfigurations, methods, standardFieldCanonicalById]);

  const searchParams = new URLSearchParams(search);
  const showRequest = searchParams.get('showRequest') === 'true';
  const changeRequestId = searchParams.get('requestId');

  const {
    pendingChangeRequests,
    pendingDeleteRequests,
    canAmendChangeRequest,
    changeRequests,
  } = useChangeRequests(props.approvalConfig?.object);

  const hasBeenDeleted = changeRequests.find(
    (cr) => cr.ChangeRequestStatus === 'approved' && cr.Type === 'delete'
  )?.Id;
  useEffect(() => {
    if (hasBeenDeleted) {
      onDeleteApproved?.();
    }
  }, [hasBeenDeleted, onDeleteApproved]);

  const inFlightChangeApproval = !!(
    pendingChangeRequests.length > 0 && props.approvalConfig?.object
  );

  const inFlightDeleteApproval = !!(
    pendingDeleteRequests.length > 0 && props.approvalConfig?.object
  );

  const viewingHistoricalChangeRequest: boolean =
    // Change request URL params only apply to forms with an approval context;
    // other forms on the same page (e.g. the notification settings modal)
    // must ignore them.
    !!props.approvalConfig?.object &&
    // If we are viewing a change request
    !!changeRequestId &&
    // And it is not in the list of pending change or delete requests.
    !pendingChangeRequests.map((cr) => cr.Id).includes(changeRequestId) &&
    !pendingDeleteRequests.map((cr) => cr.Id).includes(changeRequestId);

  const changeRequest = inFlightChangeApproval
    ? pendingChangeRequests[0]
    : pendingDeleteRequests[0];

  const { data: documentVersionFile, loading: documentVersionFileLoading } =
    useGetFileById({
      queryArgs: {
        id: changeRequest?.RequestedChanges?.FileId ?? '',
      },
      shouldSkip: !changeRequest?.RequestedChanges?.FileId,
    });

  useEffect(() => {
    if (viewingChangeRequest) {
      if (viewingHistoricalChangeRequest) {
        setToolsContent(
          `change-request:${props.approvalConfig?.object?.Id}:${changeRequestId}`
        );

        return;
      }

      if (!changeRequest) {
        return;
      }

      // Update form values with requested changes
      methods.reset(
        mapPreviewedChanges?.(
          values,
          changeRequest.RequestedChanges,
          changeRequest.requestedFileChanges.map((file) => ({
            ContentType: file?.file?.ContentType || '',
            FileName: file?.file?.FileName || '',
            FileSize: file?.file?.FileSize || 0,
            Id: file?.file?.Id || '',
            CreatedAtTimestamp: file?.file?.CreatedAtTimestamp || '',
            changeRequestFileOperation:
              file.ChangeRequestFileOperation ?? undefined,
          }))
        ) ?? {
          ...values,
          ...changeRequest.RequestedChanges,
          files: [
            ...(values?.files && !documentVersionFile?.file_by_pk
              ? values.files
              : []),
            ...changeRequest.requestedFileChanges,
            ...(documentVersionFile?.file_by_pk && !documentVersionFileLoading
              ? [documentVersionFile.file_by_pk]
              : []),
          ],
        }
      );
      setToolsContent(
        `change-request:${props.approvalConfig?.object?.Id}:${changeRequest.Id}`
      );
    } else {
      if (inFlightChangeApproval) {
        methods.reset(values);
      }
      if (
        toolsContent?.startsWith('change-request') &&
        (pendingChangeRequests[0] || pendingDeleteRequests[0])
      ) {
        setToolsContent(undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    viewingChangeRequest,
    inFlightChangeApproval,
    documentVersionFile,
    documentVersionFileLoading,
    changeRequest,
  ]);

  useEffect(() => {
    if (!toolsContent?.startsWith('change-request')) {
      setViewingChangeRequest(false);
    }
  }, [toolsContent]);

  useEffect(() => {
    if (
      showRequest &&
      changeRequests.length > 0 &&
      props.approvalConfig?.object?.Id
    ) {
      setViewingChangeRequest(true);
      setToolsContent(
        changeRequestId
          ? `change-request:${props.approvalConfig?.object?.Id}:${changeRequestId}`
          : `change-request:${props.approvalConfig?.object?.Id}`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, pendingChangeRequests, changeRequests]);

  const entityName = props.i18n.entity_name;

  const onDismiss = (saved: boolean) => {
    methods.reset();
    props.onDismiss?.(saved);
  };

  const handleSubmitWrapper = ({
    action,
    disableNotification,
  }: {
    action: SaveAction<TFieldValues>;
    disableNotification?: boolean;
  }) => {
    const handler: SubmitHandler<TFieldValues> = async (data) => {
      const beforeSave = await Promise.all(
        beforeSaveHooks.map((hook) => hook())
      );

      if (beforeSave.some((result) => !result)) {
        return false;
      }

      return mutationResultNotification<TFieldValues>({
        disableNotification,
        addNotification,
        asyncAction: async (data) => {
          try {
            const formConfig = props.parentType
              ? getFormConfigRegistry(formSectionsEnabled, modules)[
                  props.parentType
                ]
              : undefined;
            const fieldConditionGraph = buildFieldConditionGraph(
              getEffectiveFieldConditions(
                customisableFormData.formFieldConfigurations,
                formConfig,
                formSectionsEnabled
              )
            );
            const hiddenFields = props.parentType
              ? getHiddenFields({
                  formId: props.parentType,
                  customisableData: customisableFormData,
                  fieldConditionGraph,
                  currentValues: data,
                  helpers,
                  modules,
                  formSectionsEnabled,
                })
              : new Set<string>();

            let cleanupData = _.cloneDeep(data);
            // Attach-file custom fields are stored as a field-scoped relation
            // (CUS-78 `relation_custom_attribute_file`), NOT in
            // CustomAttributeData — the after-save hook (`useCustomAttributeFileSave`)
            // syncs them separately. Strip them from the entity payload so the
            // selected File objects never serialise into the entity mutation.
            // Done on the cloned payload only — the live form state is untouched,
            // so a failed save keeps the user's file selections.
            const customAttributeSchema =
              customisableFormData.customAttributeSchema;
            if (
              customAttributeSchema &&
              _.isObject(_.get(cleanupData, 'CustomAttributeData'))
            ) {
              for (const renderProp of getCustomAttributeRenderProps(
                customAttributeSchema.Schema,
                customAttributeSchema.UiSchema
              )) {
                if (renderProp.type === CustomAttributeFieldType.AttachFile) {
                  _.unset(cleanupData, [
                    'CustomAttributeData',
                    renderProp.path,
                  ]);
                }
              }
            }
            hiddenFields.forEach((fieldId) => {
              cleanupData = _.set(cleanupData, fieldId, null);
            });

            // Clear any stale CR-created flag before this submit — a gated
            // delete sets it but never routes through FormContext, so it could
            // otherwise linger and mislabel the next update (RSP-6099 I/K).
            consumeChangeRequestCreated();
            const saveResult = await action(cleanupData);

            // The save action deliberately did NOT persist (e.g. approval-config
            // edit blocked by an in-flight change request). Suppress the success
            // toast and leave the form open — the action owns whatever UI it
            // shows in its place (WOR-110).
            if (saveResult === MUTATION_NO_OP) {
              return MUTATION_NO_OP;
            }

            // The mutation resolved by RAISING A CHANGE REQUEST rather than
            // applying directly (`withChangeRequestConfirm` flagged it after a
            // confirmed replay). Show "submitted for approval" instead of a
            // false "<entity> updated successfully", and stay on the entity so
            // the now-pending (read-only) state is visible — the pending-CR
            // poll flips the form to read-only shortly after. (RSP-6099 I/K)
            if (consumeChangeRequestCreated()) {
              addNotification({
                type: 'success',
                content: st('change_request_submitted_message', {
                  entity: entityName,
                }),
              });

              // If the action persisted a new row before raising the CR (a
              // fresh savedId), link its child records now — the row exists
              // regardless of the pending change. A plain gated update has no
              // savedId and leaves them for CR approval.
              const persistedId = saveResult?.savedId;
              if (persistedId) {
                await Promise.all(
                  afterSaveHooks.map((hook) => hook(persistedId))
                );
              }

              return MUTATION_NO_OP;
            }

            // Entity persisted directly (no change request). Link any child
            // records that needed the saved id — on create the action returns
            // it; on edit it is already present in the form values.
            const savedId =
              saveResult?.savedId ??
              (_.get(cleanupData, 'Id') as string | undefined) ??
              (_.get(values, 'Id') as string | undefined);
            await Promise.all(afterSaveHooks.map((hook) => hook(savedId)));

            methods.reset();
            onDismiss(true);

            return true;
          } catch (error) {
            const mapped = mapFormError(error);

            if (mapped.kind === 'suppress') {
              // change-request / attestation: treat as a successful submission
              return true;
            }

            // RSP-5788 Phase 6a → RSP-5788 Phase 6b — typed
            // CHANGE_REQUEST_REQUIRED signal from a tRPC mutation gated by
            // `withApprovalWorkflow`. The confirm modal + retry-with-flag
            // wiring lives in `withChangeRequestConfirm` (consumed by every
            // `useUpdate*TRPC` wrapper), so by the time we reach this catch
            // block the user has already either confirmed → retry succeeded
            // → no error here, OR cancelled → the wrapper re-threw the
            // CR_REQUIRED error → we suppress the toast / Sentry capture
            // (this is expected user flow, not an error).
            if (mapped.kind === 'change-request-required') {
              // User cancelled the CR confirm modal (or confirmed → the retry
              // already succeeded). This is neither a save nor an error, so
              // produce NO toast — the previous `return false` made
              // mutationResultNotification surface a spurious "Failed to save
              // changes". (RSP-5788)
              return MUTATION_NO_OP;
            }

            handleError(error);
            if (mapped.kind === 'field') {
              setFormFieldError(methods.setError, mapped.field, {
                message: mapped.message,
                type: mapped.type,
              });
            }

            // Optimistic-lock conflicts are already rendered as the form's
            // global error above. Suppress the promise notification so it does
            // not replace that actionable message with "Unexpected error" and
            // add a second, generic failure toast.
            if (tryResolveOptimisticLockConflict(error)) {
              return MUTATION_NO_OP;
            }

            return false;
          }
        },
        entityName,
        successMessageKey:
          (saveMode ?? (values ? 'update' : 'create')) === 'update'
            ? 'update_success_message'
            : 'create_success_message',
      })(data);
    };

    return handler;
  };

  const defaultSubmitActions = [
    {
      label: st('save'),
      action: onSave,
      disableNotification: false,
    },
  ];

  const defaultOnSave = methods.handleSubmit(
    handleSubmitWrapper({ action: onSave })
  );

  const inFlightApprovalReadOnly =
    (!viewingChangeRequest && inFlightChangeApproval) ||
    (viewingChangeRequest &&
      pendingChangeRequests[0] &&
      !canAmendChangeRequest(pendingChangeRequests[0]));

  // Save state for the object-toolbar bridge store is published by
  // `ToolbarStatePublisher`, rendered below ONLY when `publishToolbarState` is
  // set (the page's primary object form, gated on the object_toolbar flag).
  // Scoping the reactive `isDirty`/`isSubmitting` subscription to that child
  // keeps every other form from subscribing — see the component's note.
  const toolbarReadOnly = Boolean(
    inFlightApprovalReadOnly || readOnly || viewingHistoricalChangeRequest
  );

  const formActions: ReactNode = (
    <FormActions
      readOnly={
        inFlightApprovalReadOnly || readOnly || viewingHistoricalChangeRequest
      }
      onDismiss={onDismiss}
      formId={props.formId}
      methods={methods}
      onDelete={onDelete}
      submitActions={(submitActions || defaultSubmitActions).map((sa) => ({
        ...sa,
        action: methods.handleSubmit(
          handleSubmitWrapper({
            action: sa.action,
            disableNotification: sa.disableNotification,
          })
        ),
      }))}
      secondaryActions={
        secondaryActions?.map((sa) => ({
          ...sa,
        })) || []
      }
    />
  );

  const body = (
    <div className={'flex-col flex gap-5'} data-testid={'form-context'}>
      <ChangeRequestAlerts
        inFlightChangeApproval={inFlightChangeApproval}
        viewingHistoricalChangeRequest={viewingHistoricalChangeRequest}
        viewingChangeRequest={viewingChangeRequest}
        onToggleView={() => setViewingChangeRequest((prev) => !prev)}
        entityName={entityName}
        inFlightApprovalReadOnly={inFlightApprovalReadOnly}
        inFlightDeleteApproval={inFlightDeleteApproval}
      />
      {props.children}
    </div>
  );

  // The before/after diff (`previewChanges`) must compare `values` against the
  // SAME reshaped payload the pending-changes form is reset to (see the
  // `methods.reset(mapPreviewedChanges(...))` effect above). Owners/contributors
  // are stored in `RequestedChanges` as separate user + group arrays, but the
  // form binds them to a single combined `{ type, value }[]` field. Diffing the
  // combined field against the raw payload made assigned groups look removed and
  // flagged unchanged fields as changed (RSP-6030). Prefer an explicit
  // `mapRequestedChanges`, then fall back to `mapPreviewedChanges`, then raw.
  const requestedChanges = pendingChangeRequests[0]?.RequestedChanges;
  const mappedRequestedChanges = !requestedChanges
    ? requestedChanges
    : mapRequestedChanges
      ? mapRequestedChanges(requestedChanges)
      : mapPreviewedChanges
        ? mapPreviewedChanges(values, requestedChanges, [])
        : requestedChanges;

  return (
    <>
      <RiskSmartFormProvider
        parentType={props.parentType}
        setCustomFormValidation={setCustomFormValidation}
        onSave={onSaveFn}
        beforeSaveHooks={beforeSaveHooks}
        afterSaveHooks={afterSaveHooks}
        setAfterSaveHooks={setAfterSaveHooks}
        setOnSave={setOnSave}
        setBeforeSaveHooks={setBeforeSaveHooks}
        previewChanges={
          inFlightChangeApproval && viewingChangeRequest && !!values
            ? mapChanges(values, {
                ...mappedRequestedChanges,
                files: pendingChangeRequests[0]?.requestedFileChanges,
              })
            : null
        }
        readOnly={
          inFlightApprovalReadOnly || readOnly || viewingHistoricalChangeRequest
        }
        defaultOnSave={defaultOnSave}
      >
        <FormProvider {...methods}>
          {publishToolbarState && (
            <ToolbarStatePublisher
              control={methods.control}
              readOnly={toolbarReadOnly}
              onSave={defaultOnSave}
            />
          )}
          {publishActiveForm && props.parentType && (
            <ActiveFormPublisher
              methods={methods}
              parentType={props.parentType}
              recordId={props.activeFormRecordId ?? null}
            />
          )}
          {/*
           * Wraps the form's own fields, not just the field renderer, so a
           * component deciding whether a field can be filled in reads the same
           * hidden set the renderer does. Evaluates live values, so it belongs
           * inside `FormProvider`. Skipped without a `parentType`: the provider
           * can resolve no conditions at all, and its value subscription costs
           * a re-render per keystroke.
           */}
          {props.parentType ? (
            <ConditionalFieldsProvider>
              {renderTemplate({
                ...props,
                actions: formActions,
                children: body,
              })}
            </ConditionalFieldsProvider>
          ) : (
            renderTemplate({ ...props, actions: formActions, children: body })
          )}
        </FormProvider>
      </RiskSmartFormProvider>
    </>
  );
};
